<template>
  <div class="actualite-area gray-bg actualite-3 section-padding pt-100">
    <div class="container">
      <div class="row mt-30 actualite-item-wrap">

        <!-- Un bloc par article -->
        <div
          class="col-md-4 mb-5"
          v-for="item in articles"
          :key="item.id"
        >
          <div class="article-stack">
            <div
              class="stack-gallery"
              v-if="item.medias && item.medias.length"
              @click="goToDetails(item.id)"
            >
              <!-- LOADER PAR ARTICLE -->
              <div
                v-if="loadingImages[item.id]"
                class="stack-loader"
              >
                <div class="spinner"></div>
                <span>Chargement…</span>
              </div>

              <!-- Images empilées -->
              <div
                v-for="(media, index) in visibleMedias(item.medias)"
                :key="media.id"
                class="stack-image"
                :class="'layer-' + (index + 1)"
              >
                <img
                  v-if="!isVideo(media.description)"
                  :src="getUrlApiForFiles(media.description)"
                  alt="aperçu"
                  loading="lazy"
                  @load="onImageLoaded(item.id, visibleMedias(item.medias).length)"
                />
              </div>

              <!-- Titre -->
              
            </div>

            <!-- Cas sans médias -->
            <div v-else class="stack-placeholder">
              <img
                v-if="item.urlImage"
                :src="getUrlApiForFiles(item.urlImage)"
                alt=""
              />
              <div v-else class="no-image">Aucune image</div>
            </div>
            <div class="article-titles">
                {{ item.titre }}
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mb-4">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ApiService from "@/services/ApiService";
import { getUrlApiForFiles, error } from "@/utils/utils";
import { defineComponent, ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import PaginationComponent from "@/components/Utilities/Pagination.vue";

export default defineComponent({
  name: "ActualitePoste",
  components: {
    PaginationComponent,
  },
  setup() {
    interface Media {
      id: number;
      description: string;
    }

    interface Article {
      id: number;
      titre: string;
      urlImage?: string;
      medias?: Media[];
    }

    const articles = ref<Article[]>([]);
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(9);
    const totalElements = ref(0);

    const router = useRouter();

    /* 🔥 loading par article */
    const loadingImages = ref<Record<number, boolean>>({});
    const loadedCount = ref<Record<number, number>>({});

    function goToDetails(articleId: number) {
      router.push({ name: "DetailsPhoto", params: { id: articleId } });
    }

    function isVideo(filename: string): boolean {
      return /\.(mp4|webm|ogg)$/i.test(filename);
    }

    function visibleMedias(medias: Media[]) {
      return medias.slice(0, 3);
    }

    function onImageLoaded(articleId: number, total: number) {
      loadedCount.value[articleId] =
        (loadedCount.value[articleId] || 0) + 1;

      if (loadedCount.value[articleId] >= total) {
        loadingImages.value[articleId] = false;
      }
    }

    function handlePaginate({ page_, limit_ }) {
      page.value = page_;
      getTreeArticle(page_, limit_);
    }

    function getTreeArticle(page = 1, limi = 9) {
      return ApiService.get(
        `/actualite/articles/43?page=${page}&limit=${limi}`
      )
        .then(({ data }) => {
          articles.value = data.data.data;

          // initialiser le loading par article
          articles.value.forEach((a) => {
            loadingImages.value[a.id] = true;
            loadedCount.value[a.id] = 0;
          });

          totalPages.value = data.data.totalPages;
          limit.value = data.data.limit;
          totalElements.value = data.data.totalElements;
        })
        .catch(({ response }) => {
          error(response.data.message);
        });
    }

    onMounted(() => {
      getTreeArticle();
    });

    return {
      articles,
      getUrlApiForFiles,
      page,
      totalPages,
      limit,
      totalElements,
      handlePaginate,
      isVideo,
      goToDetails,
      visibleMedias,
      onImageLoaded,
      loadingImages,
    };
  },
});
</script>

<style>
.stack-gallery {
  position: relative;
  width: 100%;
  height: 260px;
  cursor: pointer;
}

.stack-image {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.layer-1 {
  z-index: 3;
}
.layer-2 {
  z-index: 2;
  transform: rotate(-3deg) translateY(10px);
  opacity: 0.8;
}
.layer-3 {
  z-index: 1;
  transform: rotate(3deg) translateY(20px);
  opacity: 0.6;
}

.stack-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 🔥 LOADER */
.stack-loader {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  border-radius: 12px;
  gap: 10px;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #fff;
  border-radius: 50%;
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}


.article-titles {
  margin-top: 25px;
  text-align: center;
  font-weight: 500px;
  font-size: 20px;
  color: #0b1b3f;
}
</style>
